import type { App, Directive } from "vue";

const SKIPPED_TYPES = new Set([
  "hidden",
  "checkbox",
  "radio",
  "button",
  "submit",
  "reset",
  "file",
  "range",
  "color",
]);

// Browsers often ignore `autocomplete="off"` (Chrome in particular), but skip autofill for an
// unrecognized token. The data attributes opt out of common password managers.
const INPUT_ATTRIBUTES: Record<string, string> = {
  autocomplete: "search-filter-no-autofill",
  "data-lpignore": "true",
  "data-1p-ignore": "true",
  "data-bwignore": "true",
  "data-form-type": "other",
};

function disableOn(input: HTMLInputElement | HTMLTextAreaElement) {
  if (input instanceof HTMLInputElement && SKIPPED_TYPES.has(input.type))
    return;
  for (const [name, value] of Object.entries(INPUT_ATTRIBUTES)) {
    if (input.getAttribute(name) !== value) input.setAttribute(name, value);
  }
}

function disableWithin(root: Element) {
  if (root instanceof HTMLInputElement || root instanceof HTMLTextAreaElement)
    disableOn(root);
  root.querySelectorAll("input, textarea").forEach((input) => {
    disableOn(input as HTMLInputElement | HTMLTextAreaElement);
  });
  if (root instanceof HTMLFormElement) root.setAttribute("autocomplete", "off");
}

const observers = new WeakMap<HTMLElement, MutationObserver>();

/**
 * `v-no-autofill` on a container keeps browser/password-manager autofill out of every input in it,
 * including inputs rendered later (expansion panels, conditional fields).
 */
export const vNoAutofill: Directive<HTMLElement> = {
  mounted(el) {
    disableWithin(el);
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "attributes") {
          disableWithin(mutation.target as Element);
        } else {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof Element) disableWithin(node);
          });
        }
      }
    });
    // Watch `autocomplete` too: Vuetify re-renders can reset it on the input.
    observer.observe(el, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["autocomplete", "type"],
    });
    observers.set(el, observer);
  },
  unmounted(el) {
    observers.get(el)?.disconnect();
    observers.delete(el);
  },
};

export default {
  install(app: App) {
    app.directive("no-autofill", vNoAutofill);
  },
};

declare module "vue" {
  interface ComponentCustomProperties {
    vNoAutofill: typeof vNoAutofill;
  }
}
